import React, { useEffect, useRef } from 'react';

/** entering: mounts collapsed and expands. stable: no animation. leaving: collapses, then reports back. */
export type PanelPhase = 'entering' | 'stable' | 'leaving';

type CollapsePanelProps = {
  phase: PanelPhase;
  /** Fired once a leaving panel has collapsed, so the parent can unmount it. */
  onLeft?: () => void;
  className?: string;
};

/** Must match the .CollapsePanel transition duration in ManageSchool.scss. */
const DURATION_MS = 350;
/** Used when no transitionend arrives, e.g. the panel is hidden or the transition is interrupted. */
const FALLBACK_MS = DURATION_MS + 100;

/**
 * CSS-only height and opacity transition around a block whose height is unknown.
 * max-height stays pinned to the content, so later growth (a grid replacing its spinner)
 * slides open instead of jumping.
 */
export const CollapsePanel: React.FC<CollapsePanelProps> = ({ phase, onLeft, className, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);
  const frame = useRef<number | null>(null);
  const onLeftRef = useRef(onLeft);
  onLeftRef.current = onLeft;

  useEffect(() => {
    const el = ref.current;
    const inner = innerRef.current;
    if (!el || !inner) return;

    let removeListener: (() => void) | null = null;
    let observer: { disconnect: () => void } | null = null;

    const clear = () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      if (frame.current !== null) window.cancelAnimationFrame(frame.current);
      timer.current = null;
      frame.current = null;
      if (removeListener) {
        removeListener();
        removeListener = null;
      }
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };

    const pin = () => {
      el.style.maxHeight = inner.offsetHeight + 'px';
    };

    /** Re-pins whenever the content resizes. Without ResizeObserver the panel simply stops following. */
    const follow = () => {
      pin();
      const ResizeObserverCtor = (window as any).ResizeObserver;
      if (ResizeObserverCtor) {
        observer = new ResizeObserverCtor(pin);
        (observer as any).observe(inner);
      }
    };

    const afterTransition = (done: () => void) => {
      let finished = false;
      const onEnd = (e: TransitionEvent) => {
        if (e.target === el && e.propertyName === 'max-height') finish();
      };
      const finish = () => {
        if (finished) return;
        finished = true;
        clear();
        done();
      };
      el.addEventListener('transitionend', onEnd);
      removeListener = () => el.removeEventListener('transitionend', onEnd);
      timer.current = window.setTimeout(finish, FALLBACK_MS);
    };

    clear();

    if (phase === 'entering') {
      // Already collapsing means it was wanted back mid-flight: expand from where it is, don't snap to zero.
      if (!el.classList.contains('CPCollapsed')) {
        el.style.maxHeight = '0px';
        el.classList.add('CPCollapsed');
      }
      // Two frames: one to paint the collapsed state, one to transition from it.
      frame.current = window.requestAnimationFrame(() => {
        frame.current = window.requestAnimationFrame(() => {
          pin();
          el.classList.remove('CPCollapsed');
          afterTransition(follow);
        });
      });
      return clear;
    }

    if (phase === 'leaving') {
      pin();
      // Forces layout so the collapse starts from the pinned height rather than skipping to zero.
      void el.offsetHeight;
      frame.current = window.requestAnimationFrame(() => {
        el.style.maxHeight = '0px';
        el.classList.add('CPCollapsed');
        afterTransition(() => {
          if (onLeftRef.current) onLeftRef.current();
        });
      });
      return clear;
    }

    el.classList.remove('CPCollapsed');
    follow();
    return clear;
  }, [phase]);

  return (
    <div ref={ref} className={'CollapsePanel' + (className ? ' ' + className : '')} aria-hidden={phase === 'leaving'}>
      <div ref={innerRef} className="CPInner">
        {children}
      </div>
    </div>
  );
};
